<template>
  <v-dialog v-model="dialog" max-width="860px" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card dark class="block-dialog">
      <div class="block-dialog-header">
        <div>
          <div class="block-eyebrow">{{ studioName || "Aktuelles Studio" }}</div>
          <h2>{{ isEditing ? (editingDate ? "Tagessperre bearbeiten" : "Tage sperren") : "Gesperrte Tage" }}</h2>
        </div>
        <v-btn icon :disabled="isSaving || !!removingDate" aria-label="Dialog schliessen" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-card-text class="block-dialog-body">
        <template v-if="!isEditing">
          <div class="block-header">
            <p>Sperre alle oder einzelne Formulare an ausgewählten Tagen in diesem Studio.</p>
            <v-btn class="block-action" color="#009640" depressed :disabled="isLoading || !!loadError || !!removingDate" @click="openEditor()">
              <v-icon left>mdi-plus</v-icon>
              Tage sperren
            </v-btn>
          </div>
          <v-alert v-if="loadError" type="error" text>
            {{ loadError }}
            <v-btn text class="block-action" @click="fetchDays">Erneut laden</v-btn>
          </v-alert>
          <v-data-table
            v-else
            :headers="headers"
            :items="blockedDays"
            :loading="isLoading"
            item-key="date"
            sort-by="date"
            :items-per-page="10"
            mobile-breakpoint="720"
            no-data-text="Es sind keine Tage gesperrt."
            loading-text="Gesperrte Tage werden geladen."
            dark
            class="block-table">
            <template v-slot:item.date="{ item }">
              <strong>{{ formatDate(item.date) }}</strong>
            </template>
            <template v-slot:item.reservationTypes="{ item }">
              <span class="block-type-names">{{ typeNames(item) }}</span>
            </template>
            <template v-slot:item.message="{ item }">
              <p class="block-message">{{ item.message }}</p>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="block-row-actions">
                <v-btn icon :disabled="!!removingDate" :aria-label="'Tagessperre für ' + formatDate(item.date) + ' bearbeiten'" @click="openEditor(item)">
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn text class="block-action" :disabled="!!removingDate" :loading="removingDate === item.date" @click="removeDay(item)">Freigeben</v-btn>
              </div>
            </template>
          </v-data-table>
          <p class="block-note">Bereits gebuchte Reservationen bleiben bestehen. Prüfe sie bei Bedarf in der Reservationsübersicht.</p>
        </template>
        <div v-else class="block-editor">
          <h3>1. Formulare auswählen</h3>
          <v-checkbox v-model="allTypes" label="Alle Formulare in diesem Studio" color="primary" :disabled="isSaving" hide-details></v-checkbox>
          <v-select
            v-if="!allTypes"
            v-model="selectedTypes"
            :items="reservationTypes"
            item-text="name"
            item-value="_id"
            label="Zu sperrende Formulare"
            multiple
            chips
            deletable-chips
            filled
            class="mt-4"
            :disabled="isSaving"
            no-data-text="In diesem Studio sind noch keine Formulare vorhanden."
            persistent-hint
            hint="Nur diese Formulare werden gesperrt. Alle anderen bleiben buchbar."></v-select>
          <p v-else class="block-note">Gilt auch für Formulare, die später in diesem Studio hinzukommen.</p>

          <h3>2. Tage auswählen</h3>
          <p v-if="editingDate">{{ formatDate(editingDate) }}</p>
          <template v-else>
            <p>Wähle einen oder mehrere Tage aus. Bestehende Sperren kannst du in der Übersicht bearbeiten.</p>
            <v-date-picker
              v-model="selectedDates"
              multiple
              full-width
              no-title
              dark
              color="primary"
              locale="de-CH"
              :first-day-of-week="1"
              :min="today"
              :allowed-dates="canSelectDate"
              :disabled="isSaving"
              class="block-date-picker"></v-date-picker>
            <div class="block-selected-dates" aria-live="polite">
              <span v-if="!selectedDates.length">Noch keine Tage ausgewählt.</span>
              <v-chip v-for="date in sortedDates" :key="date" small>{{ formatDate(date) }}</v-chip>
            </div>
          </template>

          <h3>3. Kundeninfo hinterlegen</h3>
          <v-textarea
            v-model="message"
            label="Info für Kunden"
            placeholder="Wegen unseres Tags der offenen Tür ist dieses Angebot an diesem Tag nicht reservierbar. Vielen Dank für dein Verständnis!"
            :disabled="isSaving"
            :counter="500"
            maxlength="500"
            rows="3"
            auto-grow
            filled
            persistent-hint
            hint="Diese Info erscheint als Popup bei der Reservation eines gesperrten Angebots."></v-textarea>
          <v-alert v-if="saveError" type="error" text class="mt-4">{{ saveError }}</v-alert>
        </div>
      </v-card-text>
      <v-card-actions class="block-dialog-actions">
        <template v-if="isEditing">
          <v-btn text class="block-action" :disabled="isSaving" @click="isEditing = false">Zur Übersicht</v-btn>
          <v-btn color="#009640" depressed class="block-action" :loading="isSaving" :disabled="!canSave" @click="saveDays">
            {{ editingDate ? "Änderungen speichern" : "Tage sperren" }}
          </v-btn>
        </template>
        <v-btn v-else text class="block-action" :disabled="!!removingDate" @click="dialog = false">Schliessen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "BlockedReservationDays",
  data: () => ({
    blockedDays: [],
    reservationTypes: [],
    studioId: null,
    studioName: "",
    isLoading: false,
    loadError: "",
    dialog: false,
    isEditing: false,
    editingDate: null,
    allTypes: true,
    selectedTypes: [],
    selectedDates: [],
    message: "",
    isSaving: false,
    removingDate: null,
    saveError: "",
    headers: [
      { text: "Datum", value: "date" },
      { text: "Formulare", value: "reservationTypes", sortable: false },
      { text: "Info für Kunden", value: "message", sortable: false },
      { text: "Aktionen", value: "actions", sortable: false, align: "right" },
    ],
  }),
  computed: {
    today() {
      return this.$moment().format("YYYY-MM-DD")
    },
    sortedDates() {
      return [...this.selectedDates].sort()
    },
    canSave() {
      return !!this.studioId && !this.isLoading && !this.loadError && this.selectedDates.length > 0 && this.selectedDates.length <= 366 &&
        (this.allTypes || this.selectedTypes.length > 0) && !!this.message.trim() && this.message.trim().length <= 500
    },
  },
  methods: {
    open() {
      this.isEditing = false
      this.dialog = true
      this.fetchDays()
    },
    formatDate(date) {
      return this.$moment.utc(date).format("DD.MM.YYYY")
    },
    typeNames(day) {
      if (day.allTypes !== false) return "Alle Formulare"
      return (day.reservationTypes || []).map((id) => {
        const type = this.reservationTypes.find((item) => item._id === id)
        return type ? type.name : "Entferntes Formular"
      }).join(", ")
    },
    canSelectDate(date) {
      return !this.blockedDays.some((day) => day.date === date)
    },
    openEditor(day) {
      this.editingDate = day ? day.date : null
      this.selectedDates = day ? [day.date] : []
      this.allTypes = !day || day.allTypes !== false
      this.selectedTypes = day ? [...(day.reservationTypes || [])] : []
      this.message = day ? day.message : ""
      this.saveError = ""
      this.isEditing = true
    },
    async fetchDays() {
      this.isLoading = true
      this.loadError = ""
      try {
        const response = await api.reservationTool.getBlockedDays()
        this.blockedDays = response.data.days
        this.reservationTypes = response.data.types
        this.studioId = response.data.studioId
        this.studioName = response.data.studioName
      } catch (error) {
        this.loadError = "Die gesperrten Tage und Formulare konnten nicht geladen werden."
      } finally {
        this.isLoading = false
      }
    },
    async saveDays() {
      if (!this.canSave || this.isSaving) return
      this.isSaving = true
      this.saveError = ""
      try {
        await api.reservationTool.saveBlockedDays({
          dates: this.sortedDates,
          message: this.message.trim(),
          allTypes: this.allTypes,
          reservationTypes: this.allTypes ? [] : this.selectedTypes,
          studio: this.studioId,
        })
        this.isEditing = false
        this.$toast.success(this.editingDate ? "Tagessperre gespeichert." : "Die ausgewählten Tage sind gesperrt.")
      } catch (error) {
        const data = error.response && error.response.data
        this.saveError = typeof data === "string" ? data : "Die Tagessperren konnten nicht gespeichert werden."
        if (error.response && error.response.status === 409) {
          this.isEditing = false
          this.$toast.error(this.saveError)
        }
      } finally {
        await this.fetchDays()
        this.isSaving = false
      }
    },
    async removeDay(day) {
      if (this.removingDate || !confirm(`Die Sperre für ${this.typeNames(day)} am ${this.formatDate(day.date)} aufheben?`)) return
      this.removingDate = day.date
      try {
        await api.reservationTool.removeBlockedDay(day.date, this.studioId)
        this.$toast.success("Die Tagessperre wurde aufgehoben.")
      } catch (error) {
        const data = error.response && error.response.data
        this.$toast.error(typeof data === "string" ? data : "Die Tagessperre konnte nicht aufgehoben werden.")
      } finally {
        await this.fetchDays()
        this.removingDate = null
      }
    },
  },
}
</script>

<style scoped>
.block-header,
.block-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.block-header {
  flex-wrap: wrap;
  margin-bottom: 1.125rem;
}
.block-header h2,
.block-dialog-header h2 {
  margin: 0.1875rem 0 0;
  color: #f4f4f5;
  font-size: 1.25rem;
}
.block-header p,
.block-note {
  margin: 0.5rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}
.block-note {
  margin-top: 1rem;
}
.block-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.block-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}
.block-row-actions,
.block-selected-dates {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.block-row-actions {
  justify-content: flex-end;
}
.block-table {
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(20, 20, 21, 0.7) !important;
}
.block-table ::v-deep td {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  font-size: 1rem !important;
}
.block-table ::v-deep th,
.block-selected-dates .v-chip {
  font-size: 0.75rem !important;
}
.block-message {
  max-width: 40rem;
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-align: left;
}
.block-dialog {
  --border: rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1.5rem !important;
  background: #1c1c1d !important;
  font-size: 1rem;
}
.block-dialog-header,
.block-dialog-actions {
  flex: 0 0 auto;
  padding: 1.25rem 1.375rem;
}
.block-dialog-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.block-dialog-body {
  padding: 1.25rem 1.375rem !important;
  overflow-y: auto;
  color: #d4d4d8 !important;
  font-size: 1rem;
  line-height: 1.5;
}
.block-selected-dates {
  margin: 1rem 0 1.5rem;
  font-size: 0.875rem;
}
.block-dialog ::v-deep .v-messages,
.block-dialog ::v-deep .v-counter {
  font-size: 0.75rem;
  line-height: 1.5;
}
.block-dialog ::v-deep .v-label {
  font-size: 0.875rem;
}
.block-dialog ::v-deep textarea,
.block-dialog ::v-deep .v-select__selections {
  font-size: 1rem;
}
.block-date-picker ::v-deep .v-date-picker-table .v-btn,
.block-date-picker ::v-deep .v-date-picker-table th {
  font-size: 0.875rem;
}
.block-dialog-actions {
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.block-editor {
  max-width: 36rem;
  margin: 0 auto;
}
.block-editor h3 {
  margin: 1.5rem 0 1rem;
  color: #f4f4f5;
  font-size: 1rem;
}
.block-editor h3:first-child {
  margin-top: 0;
}
.block-type-names {
  display: block;
  min-width: 7rem;
  overflow-wrap: anywhere;
  font-size: 0.875rem;
}
.block-dialog ::v-deep .v-chip {
  height: auto;
  min-height: 2rem;
  font-size: 0.75rem;
  white-space: normal;
}
.block-dialog ::v-deep .v-chip__content {
  height: auto;
  padding: 0.25rem 0;
  white-space: normal;
}
@media (max-width: 37.4375rem) {
  .block-dialog {
    height: 100%;
    max-height: 100%;
    border-radius: 0 !important;
  }
  .block-dialog-body {
    padding: 1rem !important;
  }
  .block-dialog-actions .v-btn {
    flex: 1 1 auto;
    margin: 0 !important;
  }
}
</style>
